<script setup>
import { AxiosError } from 'axios';
import { reactive, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import TimeAgo from 'dashboard/components/ui/TimeAgo.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import RetinoAPI from 'dashboard/api/integrations/retino';

const props = defineProps({
  contact: {
    type: Object,
    default: () => ({}),
  },
  hook: {
    type: Object,
    default: () => ({}),
  },
});

const { locale } = useI18n();

const createDefaultState = () => ({
  isLoading: true,
  isApiError: false,
  page: 0,
  totalPages: null,
  states: [],
  tickets: [],
});

const state = reactive(createDefaultState());

// Incremented on every refresh, so responses of an outdated contact are dropped
let activeRequestId = 0;

const retinoApi = new RetinoAPI(props.hook.id);

const catchAxiosError = async request => {
  try {
    return await request();
  } catch (error) {
    if (error instanceof AxiosError) {
      // eslint-disable-next-line no-console
      console.error(
        'RetinoAPI Response:',
        error.response?.status,
        error.response?.data
      );
      state.isApiError = true;
      return null;
    }
    throw error;
  }
};

const checkApiError = response => {
  if (response?.data?.error) {
    // eslint-disable-next-line no-console
    console.error('RetinoAPI Error:', response.data.error);
    state.isApiError = true;
    return true;
  }
  return false;
};

// Retino answers with a 200 even when the payload is not the expected
// { total_pages, results } envelope, so the shape has to be checked as well
const getResults = response => {
  const results = response?.data?.results;
  if (Array.isArray(results)) return results;

  // eslint-disable-next-line no-console
  console.error('RetinoAPI Error: unexpected response body', response?.data);
  state.isApiError = true;
  return null;
};

const isNextPage = computed(() => {
  return !state.totalPages || state.page < state.totalPages;
});

const isContactSearchable = computed(() => {
  return !!props.contact.email;
});

const ticketState = ticket => {
  const theState = state.states?.find(s => s.id === ticket.state);
  if (!theState) return '';
  return theState.name?.[locale.value] || '';
};

const ticketProducts = ticket => ticket.products || [];

const ticketTotalPrice = ticket => {
  return ticketProducts(ticket)
    .reduce((total, product) => {
      if (!product.price?.with_vat) return total;
      const priceWithVat = parseFloat(product.price.with_vat);
      const amount = parseFloat(product.amount);
      return total + priceWithVat * amount;
    }, 0)
    .toFixed(2);
};

const ticketTimestamp = string => {
  return new Date(string).getTime() / 1000;
};

const ticketUrl = ticket =>
  `https://app.retino.com/tickets/detail/${ticket.id}/`;

const loadNextPage = async () => {
  const requestId = activeRequestId;
  const page = state.page + 1;

  state.isLoading = true;
  const searchResponse = await retinoApi.searchTickets({
    search: props.contact.email,
    page,
  });
  // The contact changed while the request was in flight, drop the response
  if (requestId !== activeRequestId) return;
  state.isLoading = false;

  if (checkApiError(searchResponse)) return;

  const results = getResults(searchResponse);
  if (!results) return;

  const { total_pages: totalPages } = searchResponse.data;
  state.page = page;
  // Without a page count, assume this page was the last one
  state.totalPages = Number.isInteger(totalPages)
    ? totalPages
    : page - (results.length ? 0 : 1);
  state.tickets = [...state.tickets, ...results];
};

const handleLoadNextPageClick = async () => {
  await catchAxiosError(loadNextPage);
};

const refresh = async () => {
  activeRequestId += 1;
  Object.assign(state, createDefaultState());
  if (!isContactSearchable.value) {
    state.isLoading = false;
    return;
  }
  catchAxiosError(async () => {
    const statesResponse = await retinoApi.getStates();
    if (checkApiError(statesResponse)) return;
    const states = getResults(statesResponse);
    if (!states) return;

    state.states = states;
    await loadNextPage();
  });
};

onMounted(() => {
  refresh();
});

watch(
  () => props.contact,
  (newValue, oldValue) => {
    if (newValue?.email !== oldValue?.email) refresh();
  }
);
</script>

<template>
  <div class="retino--tickets">
    <ul class="list-none">
      <li v-for="ticket in state.tickets" :key="ticket.id" class="mb-4">
        <div class="flex justify-between mb-1">
          <div>
            <a
              class="n-blue-text font-bold"
              :href="ticketUrl(ticket)"
              target="_blank"
              rel="noopener noreferrer"
              >{{ ticket.code }}</a
            >
          </div>
          <div class="font-bold">
            {{ ticketTotalPrice(ticket) }} {{ ticket.currency }}
          </div>
        </div>

        <div
          v-for="product in ticketProducts(ticket)"
          :key="product.id"
          class="truncate"
        >
          {{ parseInt(product.amount) }}
          <span v-html="$t('CONVERSATION_RETINO_TICKETS.TIMES')" />
          {{ product.name }}
        </div>

        <div class="mt-1 flex justify-between">
          <div>
            <TimeAgo
              :last-activity-timestamp="ticketTimestamp(ticket.updated_at)"
              :created-at-timestamp="ticketTimestamp(ticket.created_at)"
            />
          </div>
          <div class="italic">{{ ticketState(ticket) }}</div>
        </div>
      </li>
    </ul>

    <div class="flex justify-center">
      <div v-if="!isContactSearchable">
        {{ $t('CONVERSATION_RETINO_TICKETS.NOT_SEARCHABLE') }}
      </div>
      <div v-else-if="state.isApiError">
        {{
          $t('CONVERSATION_RETINO_TICKETS.LOAD_ERROR', {
            hookName: props.hook.settings?.name || 'Retino',
          })
        }}
      </div>
      <div v-else-if="state.totalPages === 0">
        {{ $t('CONVERSATION_RETINO_TICKETS.NO_RECORDS_FOUND') }}
      </div>
      <NextButton
        v-else-if="isNextPage"
        :is-loading="state.isLoading"
        size="xs"
        variant="faded"
        color="slate"
        @click="handleLoadNextPageClick"
      >
        {{ $t('CONVERSATION_RETINO_TICKETS.LOAD_MORE') }}
      </NextButton>
    </div>
  </div>
</template>
